/**
 * Anonymous visitor id — the `apr_aid` cookie (Wave H1).
 *
 * One uuid per browser, minted server-side in middleware.ts for every
 * non-API page (a server-set cookie survives Safari ITP's 7-day cap on
 * script-written cookies) with a client fallback here for HTML served from
 * the CDN cache without a middleware pass. It buckets the hero CTA variant
 * deterministically and is the `aid` on hero_cta_view/click — never joined
 * to a user or a phone; the attribution ref does that job anonymously.
 *
 * Pure + runtime-agnostic: imported by the edge middleware (no Node APIs),
 * the browser, and node-env tests.
 */

export const ANON_ID_COOKIE = 'apr_aid';
export const ANON_ID_MAX_AGE_S = 365 * 24 * 60 * 60;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidAnonId(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** uuid v4 from the platform crypto (browser, edge, node ≥19); manual v4
 *  assembly over getRandomValues when randomUUID is missing. */
export function mintAnonId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Parse a `Cookie:` header / `document.cookie` string for the anon id. */
export function readAnonIdFromCookieString(cookie: string | null | undefined): string | null {
  if (!cookie) return null;
  for (const part of cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== ANON_ID_COOKIE) continue;
    const value = decodeURIComponentSafe(part.slice(eq + 1).trim());
    return isValidAnonId(value) ? value : null;
  }
  return null;
}

function decodeURIComponentSafe(v: string): string {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

/** The exact attribute set middleware uses — kept here so the client
 *  fallback writes the same cookie (Lax, Secure, NOT httpOnly: the shell
 *  reads it to bucket the variant without a round-trip). */
export function anonIdCookieAttributes(secure: boolean): {
  maxAge: number;
  path: string;
  sameSite: 'lax';
  secure: boolean;
  httpOnly: false;
} {
  return { maxAge: ANON_ID_MAX_AGE_S, path: '/', sameSite: 'lax', secure, httpOnly: false };
}

/**
 * Browser side: return the cookie's id, minting + writing one when the
 * cached HTML arrived without a middleware-set cookie. Returns null outside
 * a document (SSR) so callers only touch it inside effects.
 */
export function getOrMintAnonIdInBrowser(): string | null {
  if (typeof document === 'undefined') return null;
  const existing = readAnonIdFromCookieString(document.cookie);
  if (existing) return existing;
  const id = mintAnonId();
  const secure = typeof location !== 'undefined' && location.protocol === 'https:';
  document.cookie =
    `${ANON_ID_COOKIE}=${id}; Max-Age=${ANON_ID_MAX_AGE_S}; Path=/; SameSite=Lax` +
    (secure ? '; Secure' : '');
  return id;
}
